import cloneDeep from 'lodash/cloneDeep';
import { DSV_FORMAT_CSV, DsvUtils } from '../dsv/dsv.utils';

export class CsvUtils {
  public static downloadCsv(csvContent: string, fileName: string): void {
    DsvUtils.download(csvContent, fileName, DSV_FORMAT_CSV);
  }

  public static toCsv(data: any): string {
    if (!data || data.length === 0) {
      return '';
    }
    return [Object.keys(data[0]), ...data.map((item) => Object.values(item))].map((e) => e.join(',')).join('\n');
  }

  public static generateCsv(data: any): string {
    if (!data || data.length === 0) {
      return '';
    }

    const replacer = (key: string, value: any) => {
      if (value === null) {
        return '';
      }
      if (typeof value === 'string' && value.includes(',')) {
        return value.replace(/"/g, '');
      }
      return value;
    };

    const header = Object.keys(data[0]);
    const csvRows = data.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(','));

    csvRows.unshift(header.join(','));

    return csvRows.join('\n');
  }

  public static seriesToCsv(series: { name: string; data: [number, number][] }[]): string {
    let csvString = '';
    series.forEach((serie) => {
      csvString += `${serie.name}\n`;
      csvString += `${serie.data.map((item) => item[0]).join(',')}\n`; // Join values with a comma
      csvString += `${serie.data.map((item) => item[1]).join(',')}\n`; // Join values with a comma
    });
    return csvString;
  }

  public static monitoringToCsv(aggregationType: 'AVG' | 'MIN' | 'MAX' | 'BAND', series: any[], legend?: any): string {
    let csvString = '';
    const header = ['Timestamp', 'Sortable time stamp (Unix Timestamp ms)'];
    const allDataPointsTimestamp = new Set();
    const sink: { timestamp: number; value: number; position: number }[] = [];
    let positionVal = 0;
    let deepClonedSeries = cloneDeep(series);
    deepClonedSeries = this.modifyData(deepClonedSeries, legend);

    deepClonedSeries.forEach((serie: any) => {
      if (serie?.name) {
        let headerName: string;
        if (serie?.id.endsWith('-area-min') || aggregationType === 'MIN') {
          headerName = serie.name + ' MIN';
        } else if (serie?.id.endsWith('-area-max') || aggregationType === 'MAX') {
          headerName = serie.name + ' MAX';
        } else {
          headerName = serie.name;
        }
        header.push(headerName);
        positionVal++;
        serie?.data?.forEach((dataItem: number[]) => {
          if (!Number.isNaN(dataItem[1])) {
            if (!(serie.id.endsWith('-area-min') && serie.id.endsWith('-area-max'))) {
              allDataPointsTimestamp.add(dataItem[0]);
            }
            sink.push({
              timestamp: dataItem[0],
              value: dataItem[1],
              position: positionVal,
            });
          }
        });
      }
    });

    const sortedAllDataPointsTimestamp = Array.from(allDataPointsTimestamp).sort();
    sortedAllDataPointsTimestamp.forEach((dataPoint) => {
      const dataPoints = sink.filter((sinkItem) => sinkItem?.timestamp === dataPoint);
      const row: (string | number)[] = [];
      const originalDate = new Date(dataPoints[0].timestamp);
      const formattedDate = originalDate.toDateString().slice(4) + ' ' + originalDate.toTimeString();
      row.push(formattedDate);
      row.push(dataPoints[0].timestamp);
      dataPoints.forEach((dataPoint) => {
        row[dataPoint.position + 1] = dataPoint.value;
      });
      csvString += `${row.join(',')}\n`;
    });
    return `${header.join(',')}\n` + csvString;
  }

  public static modifyData(originalArray: any[], legend?: any): any[] {
    if (!Array.isArray(originalArray) || originalArray.length === 0) {
      throw new Error('Input must be a non-empty array of objects');
    }

    const idObjects: any[] = [];
    const areaMinObjects: any[] = [];
    const areaMaxObjects: any[] = [];

    const baseIdToIndexMap = new Map<string, number>();

    // Categorize objects and populate baseIdToIndexMap
    originalArray.forEach((obj, index) => {
      const id = obj.id;
      if (!id.endsWith('-area-min') && !id.endsWith('-area-max')) {
        idObjects.push({ ...obj, name: legend?.data[index]?.name });
        baseIdToIndexMap.set(id, index);
      } else if (id.endsWith('-area-min')) {
        areaMinObjects.push(obj);
      } else if (id.endsWith('-area-max')) {
        areaMaxObjects.push(obj);
      }
    });

    // Sort areaMinObjects and areaMaxObjects by base ID
    areaMinObjects.sort((a, b) => {
      const baseIdA = a.id.replace('-area-min', '');
      const baseIdB = b.id.replace('-area-min', '');
      return baseIdA.localeCompare(baseIdB);
    });

    areaMaxObjects.sort((a, b) => {
      const baseIdA = a.id.replace('-area-max', '');
      const baseIdB = b.id.replace('-area-max', '');
      return baseIdA.localeCompare(baseIdB);
    });

    const rearrangedArray: any[] = [];

    // Merge objects into rearrangedArray in the desired order
    idObjects.forEach((baseObj) => {
      rearrangedArray.push(baseObj);

      const baseId = baseObj.id;

      // Find matching area-min and area-max objects
      const matchingAreaMin = areaMinObjects.find((obj) => obj.id === `${baseId}-area-min`);
      const matchingAreaMax = areaMaxObjects.find((obj) => obj.id === `${baseId}-area-max`);

      if (matchingAreaMin) {
        rearrangedArray.push(matchingAreaMin);
      }
      if (matchingAreaMax) {
        rearrangedArray.push(matchingAreaMax);
      }
    });

    // Update data in area-max objects based on area-min and area-max data
    rearrangedArray.forEach((obj) => {
      const id = obj.id;
      if (id.endsWith('-area-max')) {
        const baseId = id.replace('-area-max', '');

        // Find corresponding area-min and area-max objects
        const areaMinObj = areaMinObjects.find((obj) => obj.id === `${baseId}-area-min`);
        const areaMaxObj = areaMaxObjects.find((obj) => obj.id === `${baseId}-area-max`);

        if (areaMinObj && areaMaxObj) {
          const areaMinData = areaMinObj.data || [];
          const areaMaxData = areaMaxObj.data || [];
          const mergedData: [number, number][] = [];

          // Calculate area-max data by summing area-min and area-max values
          areaMaxData.forEach(([timestamp, maxValue]) => {
            const matchingMinValue = areaMinData.find(([minTimestamp]) => minTimestamp === timestamp);
            const minValue = matchingMinValue ? matchingMinValue[1] : 0;
            mergedData.push([timestamp, minValue + maxValue]);
          });

          obj.data = mergedData;
        }
      }
    });
    return rearrangedArray;
  }
}
